import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Form, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import FormResultMessage from './FormResultMessage';
import {
  fetchHVACampaigns,
  HVACampaign,
  fetchHVACampaignRiskElements,
  assignCampaignRiskElements
} from 'services/hvaservicecampaign.service';
import {
  fetchHVARiskCategories,
  fetchHVARiskElements,
  HVARiskCategory,
  HVARiskElement
} from 'services/hvarisk.service';

interface RiskElementWithCategory extends HVARiskElement {
  category?: HVARiskCategory;
}

const RiskAssignmentForm: React.FC = () => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [riskCategories, setRiskCategories] = useState<HVARiskCategory[]>([]);
  const [riskElements, setRiskElements] = useState<RiskElementWithCategory[]>(
    []
  );
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [selectedRiskElements, setSelectedRiskElements] = useState<Set<number>>(
    new Set()
  );

  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    'successupdate' | 'errorupdate'
  >('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  // Carga inicial de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar campañas
        const campaignsData = await fetchHVACampaigns();
        setCampaigns(campaignsData);

        // Cargar categorías de riesgo
        const categoriesData = await fetchHVARiskCategories();
        setRiskCategories(categoriesData);

        // Cargar elementos de riesgo
        const elementsData = await fetchHVARiskElements();

        // Enriquecer elementos con su categoría
        const enrichedElements = elementsData.map(element => {
          const category = categoriesData.find(
            cat => cat.id === element.categoryid
          );
          return { ...element, category };
        });

        setRiskElements(enrichedElements);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  // Cargar elementos de riesgo asignados cuando se selecciona una campaña
  useEffect(() => {
    const loadCampaignRiskElements = async () => {
      if (selectedCampaign !== null) {
        try {
          const assignedElementIds =
            await fetchHVACampaignRiskElements(selectedCampaign);
          setSelectedRiskElements(new Set(assignedElementIds));

          // Expandir categorías que tienen elementos seleccionados
          const categoriesWithSelection = new Set<number>();
          assignedElementIds.forEach(elementId => {
            const element = riskElements.find(el => el.id === elementId);
            if (element?.categoryid) {
              categoriesWithSelection.add(element.categoryid);
            }
          });
        } catch (error) {
          console.error('Error loading campaign risk elements:', error);
          setSelectedRiskElements(new Set());
        }
      } else {
        setSelectedRiskElements(new Set());
      }
    };

    loadCampaignRiskElements();
  }, [selectedCampaign, riskElements]);

  const handleCampaignSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCampaign(Number(event.target.value));
  };

  const handleRiskElementSelect = (elementId: number) => {
    setSelectedRiskElements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(elementId)) {
        newSet.delete(elementId);
      } else {
        newSet.add(elementId);
      }
      return newSet;
    });
  };

  const handleSelectAllInCategory = (categoryId: number) => {
    const categoryElements = riskElements.filter(
      el => el.categoryid === categoryId
    );
    const allSelected = categoryElements.every(el =>
      selectedRiskElements.has(el.id)
    );

    setSelectedRiskElements(prev => {
      const newSet = new Set(prev);
      categoryElements.forEach(el => {
        if (allSelected) {
          newSet.delete(el.id);
        } else {
          newSet.add(el.id);
        }
      });
      return newSet;
    });
  };

  const handleAssign = async () => {
    if (selectedCampaign !== null) {
      try {
        await assignCampaignRiskElements(
          selectedCampaign,
          Array.from(selectedRiskElements)
        );

        if (selectedRiskElements.size > 0) {
          setMessage(
            `Successfully assigned ${selectedRiskElements.size} risk elements to the campaign.`
          );
        } else {
          setMessage('All risk elements have been removed from the campaign.');
        }
        setMessageType('successupdate');
      } catch (error) {
        setMessage(
          'An error occurred while updating risk element assignments: ' + error
        );
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    } else {
      alert('Please select a campaign.');
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <Row className="p-0">
      <Col className="">
        <h3 className="px-2 text-secondary-darker">Campaigns</h3>
        <Card className="bg-body">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '350px', overflowY: 'scroll' }}
              >
                {campaigns.map(campaign => (
                  <Form.Check
                    key={campaign.id}
                    type="radio"
                    name="scorecard-campaigns"
                    id={`scorecard-campaign-${campaign.id}`}
                    label={`(${campaign.servicename}) - ${campaign.name}`}
                    value={campaign.id}
                    onChange={handleCampaignSelect}
                    checked={selectedCampaign === campaign.id}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>

      <Col className="risk-elements-column">
        <h3 className="px-2 text-secondary-darker">Risk Elements</h3>
        <Card className="bg-body">
          <Card.Body>
            <div
              className="scrollbar px-2"
              style={{ height: '350px', overflowY: 'scroll' }}
            >
              {riskCategories.map(category => {
                const categoryElements = riskElements.filter(
                  el => el.categoryid === category.id && el.active
                );
                const selectedCount = categoryElements.filter(el =>
                  selectedRiskElements.has(el.id)
                ).length;
                const allSelected =
                  categoryElements.length > 0 &&
                  categoryElements.every(el => selectedRiskElements.has(el.id));

                return (
                  <div key={category.id} className="mb-3">
                    <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faShieldHalved}
                          className={`me-2`}
                        />
                        <strong>{category.name}</strong>
                        {selectedCount > 0 && (
                          <Badge bg="primary" className="ms-2">
                            {selectedCount}/{categoryElements.length}
                          </Badge>
                        )}
                      </div>
                      <Form.Check
                        type="checkbox"
                        onChange={e => {
                          e.stopPropagation();
                          handleSelectAllInCategory(category.id);
                        }}
                        checked={allSelected}
                        className="mb-0"
                      />
                    </div>

                    <div className="ms-4 mt-2">
                      {categoryElements.map(element => (
                        <Form.Check
                          key={element.id}
                          type="checkbox"
                          id={`risk-${element.id}`}
                          className="mb-2"
                          onChange={() => handleRiskElementSelect(element.id)}
                          checked={selectedRiskElements.has(element.id)}
                        >
                          <Form.Check.Input
                            type="checkbox"
                            checked={selectedRiskElements.has(element.id)}
                            onChange={() => handleRiskElementSelect(element.id)}
                          />
                          <Form.Check.Label style={{ fontSize: '0.9rem' }}>
                            {element.name}
                          </Form.Check.Label>
                          {element.description && (
                            <small className="text-muted d-block ms-0">
                              {element.description}
                            </small>
                          )}
                        </Form.Check>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <div className="primary pt-3">
          <Button className="btn btn-block px-4" onClick={handleAssign}>
            <FontAwesomeIcon icon={faShieldHalved} className="me-2 fs-8" />
            Assign selected risk elements to campaign
          </Button>
        </div>
      </Col>

      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </Row>
  );
};

export default RiskAssignmentForm;
